import React, { useState, useEffect } from 'react';
import ProductModal from "./ProductModal";

export default function LocalInventoryHistoryDisplay({ historyData, refreshHistory }) {
    // State to manage modal visibility and selected item
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);

    // State to track the current time
    const [currentTime, setCurrentTime] = useState(new Date().toLocaleTimeString());

    // State for delete/restore confirmation
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [confirmItemId, setConfirmItemId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(0);
    const itemsPerPage = 10;

    // Function to open the modal with selected item
    const openModal = (item) => {
        setSelectedItem(item); // Set the item to be edited
        setIsModalOpen(true);  // Open the modal
    };

    // Function to close the modal
    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedItem(null); // Clear the selected item when modal closes
    };

    // Function to show confirmation dialog
    const showConfirmation = (action, itemId) => {
        setConfirmAction(action);
        setConfirmItemId(itemId);
        setShowConfirmDialog(true);
    };

    // Function to handle delete/restore confirmation
    const handleConfirmAction = async () => {
        if (!confirmAction || !confirmItemId) return;

        setIsLoading(true);
        try {
            const token = localStorage.getItem('jwt');
            const endpoint = confirmAction === 'delete' 
                ? '/api/localinventory/delete' 
                : '/api/localinventory/restore';

            const response = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ id: confirmItemId })
            });

            const result = await response.json();

            if (result.success) {
                // Refresh the history data
                if (refreshHistory) {
                    await refreshHistory();
                }
                alert(`Item ${confirmAction === 'delete' ? 'deleted' : 'restored'} successfully`);
            } else {
                alert(`Failed to ${confirmAction} item: ${result.message}`);
            }
        } catch (error) {
            console.error(`Error ${confirmAction}ing item:`, error);
            alert(`Error ${confirmAction}ing item. Please try again.`);
        } finally {
            setIsLoading(false);
            setShowConfirmDialog(false);
            setConfirmAction(null);
            setConfirmItemId(null);
        }
    };

    // Function to cancel confirmation
    const handleCancelAction = () => {
        setShowConfirmDialog(false);
        setConfirmAction(null);
        setConfirmItemId(null);
    };

    // Pagination calculations
    const totalItems = historyData.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = currentPage * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentItems = historyData.slice(startIndex, endIndex);

    // Pagination handlers
    const goToPage = (page) => {
        setCurrentPage(page);
    };

    const goToPrevious = () => {
        if (currentPage > 0) {
            setCurrentPage(currentPage - 1);
        }
    };

    const goToNext = () => {
        if (currentPage < totalPages - 1) {
            setCurrentPage(currentPage + 1);
        }
    };

    // Reset to first page when data changes
    useEffect(() => {
        setCurrentPage(0);
    }, [historyData]);

    // Effect to update the current time every second
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date().toLocaleTimeString());
        }, 1000);

        // Cleanup interval on component unmount
        return () => clearInterval(timer);
    }, []);

    return (
        <div className="">
            <div className="flex justify-between items-center px-4 sm:px-6 lg:px-8">
                <h1 className="text-base font-semibold text-gray-900">Inventory History</h1>
                <div className="text-sm font-medium text-gray-500">
                    Current Time: <span className="font-bold text-gray-800">{currentTime}</span>
                </div>
            </div>
            <p className="mt-2 text-sm text-gray-700 px-4 sm:px-6 lg:px-8">
                A record of recent inventory updates, including ASIN, SKU, FNSKU, and GTIN.
            </p>
            <div className="mt-8 flow-root">
                <div className="-my-2 overflow-x-auto">
                    <div className="inline-block min-w-full py-2 align-middle">
                        <table className="min-w-full divide-y divide-gray-300">
                            <thead>
                            <tr>
                                <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">Item Name</th>
                                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">FNSKU</th>
                                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">ASIN</th>
                                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">SELLER SKU</th>
                                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">CONDITION</th>
                                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">LOCATION</th>
                                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">QTY IN SECTION</th>
                                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">DATE</th>
                                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">EDIT</th>
                                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">DELETE/RESTORE</th>
                            </tr>
                            </thead>
                            <tbody className="bg-white">
                            {currentItems.map((item, index) => {
                                const isDeleted = item.deleted === 1 || item.deleted === true;
                                return (
                                <tr key={item.id || index} className={`even:bg-gray-50 ${isDeleted ? 'opacity-50 bg-gray-100' : ''}`}>
                                    <td className={`py-4 pl-4 pr-3 text-xs font-medium max-w-sm ${isDeleted ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                                        <div 
                                            className="line-clamp-2 break-words"
                                            title={item.item_name || item.seller_sku || 'N/A'}
                                        >
                                            {item.item_name || item.seller_sku || 'N/A'}
                                        </div>
                                    </td>
                                    <td className={`whitespace-nowrap px-3 py-4 text-xs ${isDeleted ? 'text-gray-400 line-through' : 'text-gray-500'}`}>{item.identifier || 'N/A'}</td>
                                    <td className={`whitespace-nowrap px-3 py-4 text-xs ${isDeleted ? 'text-gray-400 line-through' : 'text-gray-500'}`}>{item.asin || 'N/A'}</td>
                                    <td className={`whitespace-nowrap px-3 py-4 text-xs ${isDeleted ? 'text-gray-400 line-through' : 'text-gray-500'}`}>{item.seller_sku || 'N/A'}</td>
                                    <td className={`whitespace-nowrap px-3 py-4 text-xs ${isDeleted ? 'text-gray-400 line-through' : 'text-gray-500'}`}>{item.sku_condition || 'N/A'}</td>
                                    <td className={`whitespace-nowrap px-3 py-4 text-sm ${isDeleted ? 'text-gray-400 line-through' : 'text-gray-500'}`}>{item.location || 'N/A'}</td>
                                    <td className={`whitespace-nowrap px-3 py-4 text-sm font-medium ${isDeleted ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{item.qty_in_section != null ? item.qty_in_section : '—'}</td>
                                    <td className={`whitespace-nowrap px-3 py-4 text-sm ${isDeleted ? 'text-gray-400 line-through' : 'text-gray-500'}`}>
                                        {item.created_at ? new Date(item.created_at + 'Z').toLocaleString() : 'N/A'}
                                    </td>
                                    <td className="relative whitespace-nowrap py-4 px-3 text-center text-sm font-medium">
                                        <button
                                            onClick={() => openModal(item)}
                                            className={`px-3 py-1 rounded text-white text-sm font-medium ${
                                                isDeleted 
                                                    ? 'bg-gray-400 cursor-not-allowed' 
                                                    : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                                            }`}
                                            disabled={isDeleted}
                                        >
                                            Edit
                                        </button>
                                    </td>
                                    <td className="relative whitespace-nowrap py-4 px-3 text-center text-sm font-medium">
                                        <button
                                            onClick={() => showConfirmation(isDeleted ? 'restore' : 'delete', item.id)}
                                            className={`px-3 py-1 rounded text-white text-sm font-medium ${
                                                isDeleted 
                                                    ? 'bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2' 
                                                    : 'bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2'
                                            }`}
                                            disabled={isLoading}
                                        >
                                            {isDeleted ? 'Restore' : 'Delete'}
                                        </button>
                                    </td>
                                </tr>
                                );
                            })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Pagination Controls */}
            {totalItems > 0 && (
                <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
                    <div className="flex flex-1 justify-between sm:hidden">
                        <button
                            onClick={goToPrevious}
                            disabled={currentPage === 0}
                            className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Previous
                        </button>
                        <button
                            onClick={goToNext}
                            disabled={currentPage >= totalPages - 1}
                            className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Next
                        </button>
                    </div>
                    <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                        <div>
                            <p className="text-sm text-gray-700">
                                Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                                <span className="font-medium">{Math.min(endIndex, totalItems)}</span> of{' '}
                                <span className="font-medium">{totalItems}</span> results
                            </p>
                        </div>
                        <div>
                            <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                                <button
                                    onClick={goToPrevious}
                                    disabled={currentPage === 0}
                                    className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <span className="sr-only">Previous</span>
                                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                        <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                                    </svg>
                                </button>
                                
                                {/* Page Numbers */}
                                {Array.from({ length: totalPages }, (_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => goToPage(i)}
                                        className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                                            i === currentPage
                                                ? 'z-10 bg-indigo-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
                                                : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                                        }`}
                                    >
                                        {i + 1}
                                    </button>
                                ))}
                                
                                <button
                                    onClick={goToNext}
                                    disabled={currentPage >= totalPages - 1}
                                    className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <span className="sr-only">Next</span>
                                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                        <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                                    </svg>
                                </button>
                            </nav>
                        </div>
                    </div>
                </div>
            )}

            {/* Render the ProductModal only if `isModalOpen` is true */}
            {isModalOpen && (
                <ProductModal 
                    open={isModalOpen} 
                    item={selectedItem} 
                    onClose={closeModal}
                    refreshHistory={refreshHistory}
                />
            )}

            {/* Confirmation Dialog */}
            {showConfirmDialog && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                        <div className="mt-3 text-center">
                            <h3 className="text-lg font-medium text-gray-900">
                                {confirmAction === 'delete' ? 'Delete Item' : 'Restore Item'}
                            </h3>
                            <div className="mt-2 px-7 py-3">
                                <p className="text-sm text-gray-500">
                                    Are you sure you want to {confirmAction} this inventory item? 
                                    {confirmAction === 'delete' && ' This action can be undone by restoring the item.'}
                                </p>
                            </div>
                            <div className="flex justify-center gap-4 mt-4">
                                <button
                                    onClick={handleCancelAction}
                                    className="px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
                                    disabled={isLoading}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleConfirmAction}
                                    className={`px-4 py-2 text-white text-base font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 ${
                                        confirmAction === 'delete' 
                                            ? 'bg-red-600 hover:bg-red-700 focus:ring-red-300' 
                                            : 'bg-green-600 hover:bg-green-700 focus:ring-green-300'
                                    }`}
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Processing...' : (confirmAction === 'delete' ? 'Delete' : 'Restore')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
